import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { LayoutTemplate, Loader2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { AuroraLogo } from '../../office/icons';
import { mensajeDe } from '../responder/utilidades';
import '../../office/office.css';
import './buzon.css';

/*
 * «Nuevo → Buzón de quejas y sugerencias» en Drive. Dos caminos:
 *   · Aurora IA lo diseña a partir de una descripción (preguntas, categorías y plazos): POST /forms/buzon/ia
 *   · la plantilla lista para usar: POST /forms {plantilla: "buzon"}
 * Los dos nacen con el buzón de casos activo y sin publicar. `onCreado(archivo)` abre el editor.
 */

const EJEMPLOS = [
  'Residencial con caseta de vigilancia: quejas de vecinos sobre guardias, accesos, limpieza y áreas comunes',
  'Empresa de seguridad privada: clientes que reportan al personal del servicio o sugieren mejoras',
  'Buzón interno para empleados: quejas laborales, sugerencias y denuncias confidenciales',
];

export default function DialogoCrearBuzon({ open, onOpenChange, folder = '/', onCreado }) {
  const [prompt, setPrompt] = useState('');
  const [ocupado, setOcupado] = useState(null); // 'ia' | 'plantilla'
  const entrada = useRef(null);

  const crear = async (conAurora) => {
    if (ocupado || (conAurora && !prompt.trim())) return;
    setOcupado(conAurora ? 'ia' : 'plantilla');
    try {
      const { data } = conAurora
        ? await api.post('/forms/buzon/ia', { prompt: prompt.trim(), folder }, { timeout: 180000 })
        : await api.post('/forms', { folder, plantilla: 'buzon' });
      toast.success(`${data.archivo.name} creado. Publícalo para empezar a recibir casos.`);
      setPrompt('');
      onOpenChange(false);
      onCreado?.(data.archivo);
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo crear el buzón'));
    } finally {
      setOcupado(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !ocupado && onOpenChange(v)}>
      <DialogContent
        className="max-w-xl rounded-3xl zo-create"
        onOpenAutoFocus={(e) => { e.preventDefault(); entrada.current?.focus(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl"><AuroraLogo size={24} /> Nuevo buzón de quejas y sugerencias</DialogTitle>
          <DialogDescription>
            Cada mensaje recibe un folio y se asigna a una persona del equipo, con plazo, historial y seguimiento para quien lo envía.
          </DialogDescription>
        </DialogHeader>
        <form className="zo-create-body" onSubmit={(e) => { e.preventDefault(); crear(true); }}>
          <textarea
            ref={entrada}
            className="zo-create-input"
            rows={4}
            value={prompt}
            disabled={!!ocupado}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe tu organización y qué quieres recibir: áreas, servicios, tipos de queja…"
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) crear(true); }}
            aria-label="Describe el buzón"
          />
          <div className="zo-create-examples">
            {EJEMPLOS.map((ej) => <button key={ej} type="button" disabled={!!ocupado} onClick={() => setPrompt(ej)}>{ej}</button>)}
          </div>
          <div className="zo-create-footer">
            <button type="button" className="bz-boton-borde" onClick={() => crear(false)} disabled={!!ocupado}>
              {ocupado === 'plantilla' ? <Loader2 className="h-4 w-4 od-spin" /> : <LayoutTemplate className="h-4 w-4" />}
              Usar la plantilla
            </button>
            <span className="zo-muted flex-1 text-center">{ocupado === 'ia' ? 'Aurora IA está diseñando tu buzón…' : ''}</span>
            <button type="submit" className="zo-btn-primary" disabled={!prompt.trim() || !!ocupado}>
              {ocupado === 'ia' ? <Loader2 className="h-4 w-4 animate-spin" /> : <AuroraLogo size={16} mono />}
              Crear con Aurora
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
